import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import {
  User,
  googleAuth,
  testLogin as apiTestLogin,
  devLogin,
  getCurrentUser,
  logout as apiLogout,
  selectTenant as apiSelectTenant,
  setAuthToken,
  clearAuthToken,
  setOnUnauthorized,
  setOnTokenRefresh,
  setCsrfToken,
  SKIP_AUTH,
  GOOGLE_AUTH_CONFIGURED,
} from '../api/client';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasTenant: boolean;
  login: () => void;
  testLogin: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Dev-mode auth provider: auto-logs in via /auth/dev-login, no Google needed.
 */
function DevAuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Ref so the token-refresh callback always sees the latest user without a stale closure.
  const userRef = { current: user };
  userRef.current = user;

  useEffect(() => {
    setOnUnauthorized(() => { setUser(null); });
    setOnTokenRefresh(async () => {
      const tenantId = userRef.current?.tenant_id;
      if (!tenantId) return null;
      try {
        const result = await apiSelectTenant(tenantId);
        setAuthToken(result.access_token);
        setCsrfToken(result.csrf_token || null);
        setUser((prev) => prev ? { ...prev, role: result.role as string } : prev);
        return result.access_token;
      } catch {
        return null;
      }
    });
    return () => { setOnUnauthorized(null); setOnTokenRefresh(null); };
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      // Always probe /me — the HttpOnly auth cookie survives reloads even
      // though the in-memory token does not.
      try {
        const userData = await getCurrentUser();
        setCsrfToken(userData.csrf_token || null);
        setUser(userData);
        setIsLoading(false);
        return;
      } catch {
        clearAuthToken();
      }

      // Auto-login with dev endpoint
      try {
        const authResponse = await devLogin();
        setAuthToken(authResponse.access_token);
        setCsrfToken(authResponse.csrf_token || null);
        setUser(authResponse.user);
      } catch (err) {
        console.error('Dev login failed:', err);
        setError('Dev login failed');
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = useCallback(() => {}, []);
  const testLogin = useCallback(async () => {}, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try { await apiLogout(); } catch {}
    setUser(null);
    clearAuthToken();
    setIsLoading(false);
  }, []);

  const switchTenant = useCallback(async (tenantId: string) => {
    const result = await apiSelectTenant(tenantId);
    setAuthToken(result.access_token);
    setCsrfToken(result.csrf_token || null);
    const userData = await getCurrentUser();
    // /me rotates the CSRF cookie — keep in-memory token in sync
    setCsrfToken(userData.csrf_token || null);
    setUser(userData);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, isAuthenticated: !!user, hasTenant: !!user?.tenant_id, login, testLogin, logout, switchTenant, error }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Standard Google OAuth auth provider.
 */
function GoogleAuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Ref so the token-refresh callback always sees the latest user without a stale closure.
  const userRef = { current: user };
  userRef.current = user;

  // Check for existing session on mount
  useEffect(() => {
    setOnUnauthorized(() => { setUser(null); });
    setOnTokenRefresh(async () => {
      const tenantId = userRef.current?.tenant_id;
      if (!tenantId) return null;
      try {
        const result = await apiSelectTenant(tenantId);
        setAuthToken(result.access_token);
        setCsrfToken(result.csrf_token || null);
        setUser((prev) => prev ? { ...prev, role: result.role as string } : prev);
        return result.access_token;
      } catch {
        return null;
      }
    });
    return () => { setOnUnauthorized(null); setOnTokenRefresh(null); };
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      // Always probe /me on mount — the HttpOnly auth cookie is the source
      // of truth across page reloads. If unauthenticated we get a 401 and
      // simply remain logged-out.
      try {
        const userData = await getCurrentUser();
        setCsrfToken(userData.csrf_token || null);
        setUser(userData);
      } catch (err) {
        // 401 is the expected unauthenticated path; ignore the noise.
        clearAuthToken();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Classify an error thrown during the sign-in flow into a user-facing message.
  const classifySignInError = (err: unknown): string => {
    if (err instanceof TypeError || (err instanceof Error && (
      err.message.includes('network') ||
      err.message.includes('Network') ||
      err.message.includes('connection') ||
      err.message.includes('Connection') ||
      err.message.includes('Socket') ||
      err.message.includes('fetch')
    ))) {
      return 'network';
    }
    return 'unknown';
  };

  // Handle Google login success
  const handleGoogleSuccess = useCallback(async (tokenResponse: any) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: {
          Authorization: `Bearer ${tokenResponse.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get user info from Google');
      }

      const authResponse = await googleAuth(tokenResponse.access_token);

      setAuthToken(authResponse.access_token);
      setCsrfToken(authResponse.csrf_token || null);
      setUser(authResponse.user);
    } catch (err) {
      console.error('Login error:', err);
      if (classifySignInError(err) === 'network') {
        setError('network');
      } else {
        setError('Failed to sign in. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Google login hook
  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: (error: any) => {
      console.error('Google login error:', error);
      const desc: string = error?.error_description ?? error?.error ?? '';
      if (
        desc.toLowerCase().includes('network') ||
        desc.toLowerCase().includes('connection') ||
        desc.toLowerCase().includes('socket') ||
        desc === 'Connection error making token request'
      ) {
        setError('network');
      } else if (error?.error === 'access_denied') {
        setError('Access was denied. Please allow the required permissions and try again.');
      } else {
        setError('Google sign-in failed. Please try again.');
      }
    },
  });

  const login = useCallback(() => {
    setError(null);
    if (!GOOGLE_AUTH_CONFIGURED) {
      setError('Google sign-in is not configured. Please contact support.');
      return;
    }
    googleLogin();
  }, [googleLogin]);

  const testLogin = useCallback(async (username: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const authResponse = await apiTestLogin(username, password);
      setAuthToken(authResponse.access_token);
      setCsrfToken(authResponse.csrf_token || null);
      setUser(authResponse.user);
    } catch (err) {
      console.error('Test login error:', err);
      setError('Invalid username or password.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await apiLogout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      clearAuthToken();
      setIsLoading(false);
    }
  }, []);

  const switchTenant = useCallback(async (tenantId: string) => {
    const result = await apiSelectTenant(tenantId);
    setAuthToken(result.access_token);
    setCsrfToken(result.csrf_token || null);
    const userData = await getCurrentUser();
    // /me rotates the CSRF cookie — keep in-memory token in sync
    setCsrfToken(userData.csrf_token || null);
    setUser(userData);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, isAuthenticated: !!user, hasTenant: !!user?.tenant_id, login, testLogin, logout, switchTenant, error }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Delegates to DevAuthProvider when SKIP_AUTH=true, otherwise GoogleAuthProvider.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  if (SKIP_AUTH) {
    return <DevAuthProvider>{children}</DevAuthProvider>;
  }
  return <GoogleAuthProvider>{children}</GoogleAuthProvider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
